import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import { CHAT_ENTITY_TYPES } from "@bliks-io/contracts";
import { MermaidDiagram, MermaidDiagramSkeleton } from "./mermaid-diagram";
const REMARK_PLUGINS = [
    remarkGfm
];
const ENTITY_HREF = /^entity:\/\/(\w+)\/([a-f\d]{24})$/i;
const MERMAID_CLASS = "language-mermaid";
/**
 * ReactMarkdown's default transform drops hrefs whose protocol it does not
 * recognise, which would strip the `entity://` links the assistant emits before
 * the link renderer ever sees them. Only that one protocol is excepted — the
 * rest of the allowlist still guards every href the model authors, which is
 * steered by tenant text and by user-supplied filenames.
 */ const keepEntityLinks = (url)=>url.startsWith("entity://") ? url : defaultUrlTransform(url);
/** Extract the plain text ReactMarkdown handed to a renderer. */ function extractText(children) {
    if (typeof children === "string") {
        return children;
    }
    if (Array.isArray(children)) {
        return children.map((c)=>typeof c === "string" ? c : String(c)).join("");
    }
    return String(children);
}
/**
 * Wide GFM tables must scroll inside the turn rather than widen the transcript.
 * Static, so it is hoisted out of the per-render components map.
 */ const renderTable = ({ children })=>/*#__PURE__*/ _jsx("div", {
        className: "overflow-x-auto",
        children: /*#__PURE__*/ _jsx("table", {
            children: children
        })
    });
/** A line that opens or closes a fenced code block, per CommonMark. */ const FENCE_LINE = /^ {0,3}(`{3,}|~{3,})/;
/**
 * Offset of the opening fence CHARACTER of an unterminated code block, or `null`
 * when every fence in the document is closed.
 *
 * The fence character rather than its line: micromark tokenizes the up-to-three
 * spaces CommonMark allows before a fence as a separate event that precedes the
 * code block, so the mdast node this is compared against starts at the first
 * backtick. An indented fence — ordinary inside a numbered list — would
 * otherwise miss the comparison by the indent width.
 *
 * Streaming is why this exists: markdown parses an unterminated fence as a code
 * block running to the end of the document, so a half-arrived ```mermaid block
 * is indistinguishable from a finished one by the parsed tree alone. Feeding
 * mermaid a truncated source would show a syntax-error fallback that flips to a
 * diagram a token later, so the renderer waits for the closing fence instead.
 *
 * At most one fence can be open, and only the last one, so a single scan
 * answers it. Lines are walked by index rather than `split` — this runs on
 * every streamed token, and the transcript should not allocate an array per
 * token to answer one question.
 */ function unterminatedFenceOffset(text) {
    let lineStart = 0;
    let openOffset = null;
    let openFence = "";
    while(lineStart <= text.length){
        const lineEnd = text.indexOf("\n", lineStart);
        const line = text.slice(lineStart, lineEnd === -1 ? undefined : lineEnd);
        const match = FENCE_LINE.exec(line);
        if (match) {
            const fence = match[1];
            if (openOffset === null) {
                openOffset = lineStart + match[0].length - fence.length;
                openFence = fence;
            } else if (fence[0] === openFence[0] && fence.length >= openFence.length) {
                // A closing fence must use the same character and be no shorter.
                openOffset = null;
            }
        }
        if (lineEnd === -1) break;
        lineStart = lineEnd + 1;
    }
    return openOffset;
}
/**
 * A mermaid block replaces the whole `<pre><code>` pair, so the `<pre>` wrapper
 * is dropped for it: `white-space: pre` would otherwise apply to the rendered
 * SVG, and a block element inside `<pre>` is invalid markup. The fallback path
 * inside `MermaidDiagram` brings its own `<pre>` back.
 */ const renderPre = ({ node, children })=>{
    const child = node?.children[0];
    const className = child?.type === "element" && child.tagName === "code" ? child.properties.className : undefined;
    const isMermaid = Array.isArray(className) && className.includes(MERMAID_CLASS);
    return isMermaid ? children : /*#__PURE__*/ _jsx("pre", {
        children: children
    });
};
/** With mermaid off there is nothing to unwrap. */ const renderPlainPre = ({ children })=>/*#__PURE__*/ _jsx("pre", {
        children: children
    });
/**
 * The assistant's markdown body: GFM plus the `entity://` link protocol that
 * turns a reference into an inline entity card.
 *
 * Own component because the transcript re-renders on every streamed token, and
 * the plugin list and components map must not be rebuilt each time; the
 * fenced-code interceptor for mermaid extends the same map.
 */ export function ChatMarkdown({ text, renderEntityCard, renderMermaid = true, entityLinks = true }) {
    // Constant for a finished message, and constant across the tokens of a
    // streaming one while a fence stays open, so the components map below is not
    // rebuilt per token.
    const openFenceOffset = useMemo(()=>unterminatedFenceOffset(text), [
        text
    ]);
    const components = useMemo(()=>({
            table: renderTable,
            pre: renderMermaid ? renderPre : renderPlainPre,
            code: ({ node, className, children, ...props })=>{
                if (!renderMermaid || className !== MERMAID_CLASS) {
                    return /*#__PURE__*/ _jsx("code", {
                        className: className,
                        ...props,
                        children: children
                    });
                }
                const stillStreaming = node?.position?.start.offset === openFenceOffset;
                return stillStreaming ? /*#__PURE__*/ _jsx(MermaidDiagramSkeleton, {}) : /*#__PURE__*/ _jsx(MermaidDiagram, {
                    source: extractText(children).replace(/\n$/, "")
                });
            },
            // The assistant never has an image to show, and a model-authored
            // `<img src>` is a zero-click request to a host the model chose — an
            // exfiltration channel for anything a prompt-injected turn just read,
            // persisted in the transcript and re-fired on every reload. Rendered as
            // its alt text instead.
            img: ({ alt })=>/*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground",
                    children: alt
                }),
            a: ({ href, children })=>{
                const match = renderEntityCard && href?.startsWith("entity://") ? ENTITY_HREF.exec(href) : null;
                if (match && renderEntityCard) {
                    const [, type, id] = match;
                    if (CHAT_ENTITY_TYPES.includes(type)) {
                        return renderEntityCard({
                            type: type,
                            id,
                            name: extractText(children)
                        });
                    }
                }
                return /*#__PURE__*/ _jsx("a", {
                    href: href,
                    className: "font-medium text-foreground underline hover:no-underline",
                    target: "_blank",
                    rel: "noopener noreferrer",
                    children: children
                });
            }
        }), [
        renderEntityCard,
        renderMermaid,
        openFenceOffset
    ]);
    return /*#__PURE__*/ _jsx(ReactMarkdown, {
        remarkPlugins: REMARK_PLUGINS,
        urlTransform: entityLinks ? keepEntityLinks : defaultUrlTransform,
        components: components,
        children: text
    });
}

//# sourceMappingURL=chat-markdown.js.map